import { usePreferencesStore } from './preferences.store';
import { useShallow } from 'zustand/react/shallow';

// Theme selectors
export const useThemeMode = () =>
  usePreferencesStore((state) => state.theme.mode);
export const useColorScheme = () =>
  usePreferencesStore((state) => state.theme.colorScheme);
export const useFontSize = () =>
  usePreferencesStore((state) => state.theme.fontSize);
export const useReducedMotion = () =>
  usePreferencesStore((state) => state.theme.reducedMotion);
export const useHighContrast = () =>
  usePreferencesStore((state) => state.theme.highContrast);
export const useEffectiveTheme = () =>
  usePreferencesStore((state) => state.getEffectiveTheme());

export const useThemeSettings = () =>
  usePreferencesStore(
    useShallow((state) => ({
      mode: state.theme.mode,
      colorScheme: state.theme.colorScheme,
      fontSize: state.theme.fontSize,
      reducedMotion: state.theme.reducedMotion,
      highContrast: state.theme.highContrast,
      effectiveTheme: state.getEffectiveTheme(),
    }))
  );

// Layout selectors
export const useViewDensity = () =>
  usePreferencesStore((state) => state.layout.viewDensity);
export const useShowBreadcrumbs = () =>
  usePreferencesStore((state) => state.layout.showBreadcrumbs);
export const useShowStatusBar = () =>
  usePreferencesStore((state) => state.layout.showStatusBar);

export const useLayoutPreferences = () =>
  usePreferencesStore(
    useShallow((state) => ({
      viewDensity: state.layout.viewDensity,
      showBreadcrumbs: state.layout.showBreadcrumbs,
      showStatusBar: state.layout.showStatusBar,
      gridViewColumns: state.layout.gridViewColumns,
      listViewDensity: state.layout.listViewDensity,
    }))
  );

// Customization selectors
export const useFavoriteProjects = () =>
  usePreferencesStore((state) => state.customizations.favoriteProjects);
export const useRecentColors = () =>
  usePreferencesStore((state) => state.customizations.recentColors);
export const useDashboardLayout = () =>
  usePreferencesStore((state) => state.customizations.dashboardLayout);

export const useIsFavoriteProject = (projectId: string) =>
  usePreferencesStore((state) => state.isFavoriteProject(projectId));

export const useRenderDefaults = () =>
  usePreferencesStore(
    useShallow((state) => ({
      quality: state.customizations.defaultRenderSettings.quality,
      autoSave: state.customizations.defaultRenderSettings.autoSave,
      showNotifications:
        state.customizations.defaultRenderSettings.showNotifications,
    }))
  );

export const useWorkspacePreferences = () =>
  usePreferencesStore(
    useShallow((state) => ({
      autoOpenLastProject:
        state.customizations.workspacePreferences.autoOpenLastProject,
      confirmBeforeDelete:
        state.customizations.workspacePreferences.confirmBeforeDelete,
      autoSaveInterval:
        state.customizations.workspacePreferences.autoSaveInterval,
      showTips: state.customizations.workspacePreferences.showTips,
    }))
  );

// UI state selectors
export const useIsEditingPreferences = () =>
  usePreferencesStore((state) => state.ui.isEditingPreferences);
export const usePreferencesPanel = () =>
  usePreferencesStore((state) => state.ui.preferencesPanel);
export const useHasUnsavedChanges = () =>
  usePreferencesStore((state) => state.ui.unsavedChanges);
export const useLastSaved = () =>
  usePreferencesStore((state) => state.ui.lastSaved);

// Action selectors
export const useThemeActions = () =>
  usePreferencesStore(
    useShallow((state) => ({
      setThemeMode: state.setThemeMode,
      setColorScheme: state.setColorScheme,
      setFontSize: state.setFontSize,
      toggleReducedMotion: state.toggleReducedMotion,
      toggleHighContrast: state.toggleHighContrast,
      applyTheme: state.applyTheme,
    }))
  );

export const useLayoutActions = () =>
  usePreferencesStore(
    useShallow((state) => ({
      setViewDensity: state.setViewDensity,
      toggleBreadcrumbs: state.toggleBreadcrumbs,
      toggleStatusBar: state.toggleStatusBar,
      setGridColumns: state.setGridColumns,
      setListDensity: state.setListDensity,
    }))
  );

export const useCustomizationActions = () =>
  usePreferencesStore(
    useShallow((state) => ({
      toggleFavoriteProject: state.toggleFavoriteProject,
      addRecentColor: state.addRecentColor,
      setCustomShortcut: state.setCustomShortcut,
      removeCustomShortcut: state.removeCustomShortcut,
      setDashboardLayout: state.setDashboardLayout,
      setDefaultQuality: state.setDefaultQuality,
      toggleAutoSave: state.toggleAutoSave,
      toggleRenderNotifications: state.toggleRenderNotifications,
    }))
  );

export const usePreferencesActions = () =>
  usePreferencesStore(
    useShallow((state) => ({
      openPreferencesPanel: state.openPreferencesPanel,
      closePreferencesPanel: state.closePreferencesPanel,
      savePreferences: state.savePreferences,
      exportPreferences: state.exportPreferences,
      importPreferences: state.importPreferences,
      resetTheme: state.resetTheme,
      resetLayout: state.resetLayout,
      resetCustomizations: state.resetCustomizations,
      resetAll: state.resetAll,
    }))
  );

// Combined selector for migration support
export const usePreferences = () =>
  usePreferencesStore(
    useShallow((state) => ({
      // Theme
      theme: state.theme,
      effectiveTheme: state.getEffectiveTheme(),

      // Layout
      layout: state.layout,

      // Customizations
      customizations: state.customizations,

      // UI state
      isEditingPreferences: state.ui.isEditingPreferences,
      hasUnsavedChanges: state.ui.unsavedChanges,

      // Common actions
      setThemeMode: state.setThemeMode,
      toggleFavoriteProject: state.toggleFavoriteProject,
      savePreferences: state.savePreferences,
    }))
  );
